import {
  Divider,
  Grid,
  IconButton,
  makeStyles,
  useTheme,
} from '@material-ui/core';
import {
  mdiCancel,
  mdiDelete,
  mdiDotsVertical,
  mdiPencil,
  mdiPencilOff,
} from '@mdi/js';
import Icon from '@mdi/react';
import TextInput from 'components/TextInput/TextInput';
import { DEFAULT_ADDITIONAL_SALARY_TYPES } from 'LixaniAPI/enums';
import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  removeEmployerAllowanceDraft,
  updateEmployerAllowanceDraft,
} from 'store/employerAllowances/actions/employerAllowances.actions';
import {
  selectEmployerAllowanceDraftsIds,
  selectEmployerAllowancesIds,
  selectIsEmployerAllowancesSavePending,
} from 'store/employerAllowances/selectors/employerAllowances.selectors';
import { createEmployerAllowanceDraft } from 'store/employerAllowances/thunks/createEmployerAllowanceDraft.thunk';
import { selectSelectedProjectIntegrationPartners } from 'store/projects/selectors/projectsSelectors';
import { EmployerAllowancesTypeSelect } from './EmployerAllowanceTypeSelect';
import ActionContainer from 'components/ActionContainer/ActionContainer';
import { useTranslation } from 'react-i18next';

const useStyles = makeStyles((theme) => ({
  wrapper: {
    margin: theme.spacing(0.5),
  },
  IconContainer: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));

export const EmployerAllowanceEditItem = ({ employerAllowance }) => {
  const dispatch = useDispatch();

  const theme = useTheme();
  const classes = useStyles(theme);

  const { t } = useTranslation('common');

  const salaryTypeOptions = {
    MEAL_ALLOWANCE: {
      salaryName: t('report.salaries.meal_allowance'),
      salaryType: 'MEAL_ALLOWANCE',
    },
    FULL_DAILY_ALLOWANCE: {
      salaryName: t('report.salaries.full_daily_allowance'),
      salaryType: 'FULL_DAILY_ALLOWANCE',
    },
    DAILY_ALLOWANCE_HALF_DAY: {
      salaryName: t('report.salaries.daily_allowance_half_day'),
      salaryType: 'DAILY_ALLOWANCE_HALF_DAY',
    },
    KILOMETER_ALLOWANCE: {
      salaryName: t('report.salaries.kilometer_allowance'),
      salaryType: 'KILOMETER_ALLOWANCE',
    },
    TRAVEL_EXPENSE_COMPENSATION: {
      salaryName: t('report.salaries.travel_expense_compensation'),
      salaryType: 'TRAVEL_EXPENSE_COMPENSATION',
    },
    TAXABLE_REIMBURSEMENT_OF_EXPENSES: {
      salaryName: t('report.salaries.taxable_reimbursement_of_expenses'),
      salaryType: 'TAXABLE_REIMBURSEMENT_OF_EXPENSES',
    },
    WORKING_HOUR_REDUCTION_COMPENSATION: {
      salaryName: t('report.salaries.working_hour_reduction_compensation'),
      salaryType: 'WORKING_HOUR_REDUCTION_COMPENSATION',
    },
    TRAVEL_HOUR: {
      salaryName: t('report.salaries.travel_hour'),
      salaryType: 'TRAVEL_HOUR',
    },
    ADDITIONAL_WORK_HOUR: {
      salaryName: t('report.salaries.additional_work_hour'),
      salaryType: 'ADDITIONAL_WORK_HOUR',
    },
    CONTRACT_PAY: {
      salaryName: t('report.salaries.contract_pay'),
      salaryType: 'CONTRACT_PAY',
    },
    CONTRACT_WORK_HOUR: {
      salaryName: t('report.salaries.contract_work_hour'),
      salaryType: 'CONTRACT_WORK_HOUR',
    },
    NATIONAL_HOLIDAY_COMPENSATION: {
      salaryName: t('report.salaries.national_holiday_compensation'),
      salaryType: 'NATIONAL_HOLIDAY_COMPENSATION',
    },
    SUNDAY_WORK_COMPENSATION: {
      salaryName: t('report.salaries.sunday_work_compensation'),
      salaryType: 'SUNDAY_WORK_COMPENSATION',
    },
    CHILD_CARE_LEAVE: {
      salaryName: t('report.salaries.child_care_leave'),
      salaryType: 'CHILD_CARE_LEAVE',
    },
    MILITARY_REFRESHER_WAGE: {
      salaryName: t('report.salaries.military_refresher_wage'),
      salaryType: 'MILITARY_REFRESHER_WAGE',
    },
    MILITARY_SERVICE: {
      salaryName: t('report.salaries.military_service'),
      salaryType: 'MILITARY_SERVICE',
    },
    PARENTAL_LEAVE: {
      salaryName: t('report.salaries.parental_leave'),
      salaryType: 'PARENTAL_LEAVE',
    },
    HOLIDAY_BONUS_LEAVE: {
      salaryName: t('report.salaries.holiday_bonus_leave'),
      salaryType: 'HOLIDAY_BONUS_LEAVE',
    },
    STRIKE: {
      salaryName: t('report.salaries.strike'),
      salaryType: 'STRIKE',
    },
    TEMPORARY_LAY_OFF: {
      salaryName: t('report.salaries.temporary_lay_off'),
      salaryType: 'TEMPORARY_LAY_OFF',
    },
    STUDY_LEAVE: {
      salaryName: t('report.salaries.study_leave'),
      salaryType: 'STUDY_LEAVE',
    },
    INJURY_LEAVE: {
      salaryName: t('report.salaries.injury_leave'),
      salaryType: 'INJURY_LEAVE',
    },
    TEMPORARY_CHILD_CARE_LEAVE: {
      salaryName: t('report.salaries.temporary_child_care_leave'),
      salaryType: 'TEMPORARY_CHILD_CARE_LEAVE',
    },
    OTHER_PAID_LEAVE: {
      salaryName: t('report.salaries.other_paid_leave'),
      salaryType: 'OTHER_PAID_LEAVE',
    },
    OTHER_UNPAID_LEAVE: {
      salaryName: t('report.salaries.other_unpaid_leave'),
      salaryType: 'OTHER_UNPAID_LEAVE',
    },
    OVERTIME_LEAVE: {
      salaryName: t('report.salaries.overtime_leave'),
      salaryType: 'OVERTIME_LEAVE',
    },
    WORK_SALARY_HOURLY: {
      salaryName: t('report.salaries.salary_hour'),
      salaryType: 'WORK_SALARY_HOURLY',
    },
    WORK_SALARY_MONTHLY: {
      salaryName: t('report.salaries.salary_month'),
      salaryType: 'WORK_SALARY_MONTHLY',
    },
    HOLIDAY_PAY: {
      salaryName: t('report.salaries.holiday_pay'),
      salaryType: 'HOLIDAY_PAY',
    },
    HOLIDAY_UNPAID: {
      salaryName: t('report.salaries.holiday_unpaid'),
      salaryType: 'HOLIDAY_UNPAID',
    },
    SICK_LEAVE: {
      salaryName: t('report.salaries.sick_leave'),
      salaryType: 'SICK_LEAVE',
    },
    WORKING_HOUR_REDUCTION_LEAVE: {
      salaryName: t('report.salaries.working_hour_reduction_leave'),
      salaryType: 'WORKING_HOUR_REDUCTION_LEAVE',
    },
    PUBLIC_HOLIDAY_LEAVE: {
      salaryName: t('report.salaries.public_holiday_leave'),
      salaryType: 'PUBLIC_HOLIDAY_LEAVE',
    },
  };

  const employerAllowanceDraftsIds = useSelector(
    selectEmployerAllowanceDraftsIds
  );

  const employerAllowanceIds = useSelector(selectEmployerAllowancesIds);
  const isExistingEmployerAllowance = employerAllowanceIds.includes(
    employerAllowance.id
  );

  const isInDraft = employerAllowanceDraftsIds.includes(employerAllowance.id);
  const isSavePending = useSelector(selectIsEmployerAllowancesSavePending);

  const businessIntegrationPartners = useSelector(
    selectSelectedProjectIntegrationPartners
  );

  const handleChange = (value, attributeName) => {
    dispatch(
      updateEmployerAllowanceDraft({
        id: employerAllowance.id,
        changes: { [attributeName]: value },
      })
    );
  };

  const removeDraft = (draftId) => {
    dispatch(removeEmployerAllowanceDraft(draftId));
  };

  const setToDraft = (employerAllowance) => {
    dispatch(
      createEmployerAllowanceDraft({
        employerAllowanceAttributes: employerAllowance,
      })
    );
  };

  const setAsDeleted = () => {
    const now = new Date().toISOString();
    if (!isInDraft) {
      dispatch(
        createEmployerAllowanceDraft({
          employerAllowanceAttributes: employerAllowance,
        })
      ).then(() => {
        dispatch(
          updateEmployerAllowanceDraft({
            id: employerAllowance.id,
            changes: { deletedAt: now },
          })
        );
      });
    } else {
      dispatch(
        updateEmployerAllowanceDraft({
          id: employerAllowance.id,
          changes: { deletedAt: now },
        })
      );
    }
  };

  useEffect(() => {
    //handle automatic default salary code setting when salaryType is changed
    if (
      DEFAULT_ADDITIONAL_SALARY_TYPES[employerAllowance.salaryType] &&
      isInDraft
    ) {
      const effectiveSalaryCode = businessIntegrationPartners.includes('Fennoa')
        ? DEFAULT_ADDITIONAL_SALARY_TYPES[employerAllowance.salaryType]
            .fennoaSalaryCode
        : DEFAULT_ADDITIONAL_SALARY_TYPES[employerAllowance.salaryType]
            .procountorSalaryCode;

      dispatch(
        updateEmployerAllowanceDraft({
          id: employerAllowance.id,
          changes: { integrationSalaryCode: effectiveSalaryCode },
        })
      );
    }
  }, [employerAllowance.salaryType]);

  const isAbsenceTypeAllowance =
    DEFAULT_ADDITIONAL_SALARY_TYPES[employerAllowance.salaryType]?.baseType ===
    'ABSENCE';

  const disabled = !isInDraft || isSavePending;

  console.log('employerAllowance', employerAllowance);

  return (
    // <div className={classes.wrapper}>
    <ActionContainer
      title={`${
        salaryTypeOptions[employerAllowance.salaryType]?.salaryName ||
        'Uusi palkkalaji'
      }`}
      // closable
      // closableDisabled={hasAnyError}
      // open={open}
      // setOpen={handleOpen}
      color="main"
      actionIcon={
        !isInDraft ? (
          <Icon path={mdiPencil} size={1} />
        ) : (
          <Icon path={mdiPencilOff} size={1} />
        )
      }
      onAction={() => {
        if (!isInDraft) {
          setToDraft(employerAllowance);
        } else {
          removeDraft(employerAllowance.id);
        }
      }}
      popperIcon={<Icon path={mdiDotsVertical} size={1} />}
      popperChildren={<Icon path={mdiDelete} size={1} color="red" />}
      onPopper={() => setAsDeleted(employerAllowance.id)}
      // titleIcon={titleIcon}
      // titleIconAlign="right"
    >
      <Grid item xs={12} md={6}>
        <EmployerAllowancesTypeSelect
          selectedValue={employerAllowance.salaryType}
          handleChange={handleChange}
          disabled={disabled || isExistingEmployerAllowance}
        ></EmployerAllowancesTypeSelect>
      </Grid>
      <Grid item xs={6} md={2}>
        <TextInput
          value={employerAllowance.unit ? employerAllowance.unit : ''}
          fullWidth
          label={'Yksikkö'}
          shrink
          onChange={(event) => handleChange(event.target.value, 'unit')}
          disabled={disabled || isAbsenceTypeAllowance}
        />
      </Grid>
      <Grid item xs={6} md={4}>
        <TextInput
          value={
            employerAllowance.integrationSalaryCode
              ? employerAllowance.integrationSalaryCode
              : ''
          }
          fullWidth
          shrink
          label={'Palkkalajin koodi'}
          onChange={(event) =>
            handleChange(event.target.value, 'integrationSalaryCode')
          }
          disabled={disabled}
        />
      </Grid>
      {/* <Grid item xs={12} md={2}>
        <div className={classes.IconContainer}>
          {!isInDraft ? (
            <IconButton onClick={() => setToDraft(employerAllowance)}>
              <Icon path={mdiPencil} size={1} />
            </IconButton>
          ) : (
            <React.Fragment>
              <IconButton onClick={() => removeDraft(employerAllowance.id)}>
                <Icon path={mdiCancel} size={1} />
              </IconButton>
              {isExistingEmployerAllowance ? (
                <IconButton onClick={() => setAsDeleted(employerAllowance.id)}>
                  <Icon path={mdiDelete} size={1} color="red" />
                </IconButton>
              ) : null}
            </React.Fragment>
          )}
        </div>
      </Grid> */}
    </ActionContainer>
    // {/* <Divider style={{ margin: '10px 0 10px 0' }}></Divider> */}
    // </div>
  );
};
